/**
 * Single source of truth for the two grouped navigation clusters
 * (Calculate, Compare). Consumed by:
 *   - SharedHeader desktop dropdowns
 *   - MobileMoreSheet grouped sections
 *   - CalcNav / CompareNav on-page segmented controls
 * so the routes + labels can never drift between surfaces. The first
 * entry of each group is its default/overview destination (what the
 * parent nav item links to and what the dropdown repeats as its first
 * row, per the APG disclosure-nav pattern).
 */
import type { TranslationKey } from "@/lib/i18n";

export interface NavGroupItem {
  href: string;
  labelKey: TranslationKey;
  /** Shows a small "Pro" pill next to the label. */
  pro?: boolean;
  /** Listed only while this feature flag is on (GET /api/features). */
  feature?: "home_finder";
}

// Ordered most-used first. /calculate is the default (price valuation).
/** The two ways in on /analyze: a listing link, or the AI report over
 *  the person's own numbers (owner, 2026-10-10: "add the analyst and
 *  Analyze link into one page", pills like the calculators'). */
export const ANALYZE_TABS: NavGroupItem[] = [
  { href: "/analyze", labelKey: "nav.analyze" },
  { href: "/analyst", labelKey: "nav.analyst" },
];
export const CALC_TABS: NavGroupItem[] = [
  { href: "/calculate", labelKey: "calc.mode.price" },
  { href: "/calculate/mortgage", labelKey: "calc.mode.mortgage" },
  { href: "/calculate/rent-vs-buy", labelKey: "calc.mode.rentbuy" },
  { href: "/calculate/affordability", labelKey: "calc.mode.afford" },
  { href: "/calculate/sale-tax", labelKey: "calc.mode.tax" },
  { href: "/calculate/eligibility", labelKey: "calc.mode.eligibility" },
];

// /compare is the default (listing comparison).
export const COMPARE_TABS: NavGroupItem[] = [
  { href: "/compare", labelKey: "compare.tab.listings" },
  { href: "/compare/residences", labelKey: "compare.tab.residences" },
];


// Map layers - path-based so each one is its own indexable URL (the
// legacy ?layer= param still resolves for old links). The bare /map is
// the listings layer (the default/overview). Ordered by use (owner,
// 2026-10-11): production page views over 90 days to 2026-10-11 were
// listings 2 171, zones 567, residences 491, hexes 231, districts 115,
// my map 90; demand and invest are new and follow. The land map was
// retired on 2026-10-11 (plots live on the listings layer).
export const MAP_TABS: NavGroupItem[] = [
  // By 90-day production page views, then the new investment and demand
  // layers ahead of districts and my map (owner, 2026-10-11); the map
  // page's picker follows the same order.
  { href: "/map", labelKey: "map.layer.listings" },
  { href: "/map/zones", labelKey: "map.layer.zones" },
  { href: "/map/residences", labelKey: "map.layer.residences" },
  { href: "/map/hexes", labelKey: "map.layer.hexes" },
  { href: "/map/invest", labelKey: "map.layer.invest" },
  { href: "/map/demand", labelKey: "map.layer.demand" },
  { href: "/map/districts", labelKey: "map.layer.districts" },
  { href: "/map/personal", labelKey: "map.layer.personal" },
  // The 3D buildings layer is off every menu (owner, 2026-10-11).
];


// ── The site's navigation, one definition (owner, 2026-10-07) ─────────
// Six groups: the desktop bar renders them as dropdowns, the phone
// "More" sheet as headed sections, so both always list the same things.
// A group's `href` is where its label links; sections may carry a
// heading.

export interface NavSection {
  headingKey?: TranslationKey;
  items: NavGroupItem[];
}

export interface NavGroupDef {
  key: "valuate" | "calculate" | "listings" | "map" | "market" | "company";
  href: string;
  labelKey: TranslationKey;
  sections: NavSection[];
}

// Valuation (a listing, the AI report, the assistant) and the
// calculators are two groups, not one (owner, 2026-10-11: "valuation
// and calculate should be separate"); Compare lives under Listings.
export const NAV_GROUPS: NavGroupDef[] = [
  {
    key: "valuate",
    href: "/analyze",
    labelKey: "nav.valuate",
    sections: [{
      items: [
        { href: "/analyze", labelKey: "nav.check" },
        { href: "/analyst", labelKey: "nav.analyst" },
        { href: "/chat", labelKey: "nav.assistant" },
      ],
    }],
  },
  {
    key: "calculate",
    href: "/calculate",
    labelKey: "nav.calculators",
    sections: [{ items: [{ href: "/calculate", labelKey: "nav.value_home" }, ...CALC_TABS.slice(1)] }],
  },
  {
    key: "listings",
    href: "/listings",
    labelKey: "nav.listings",
    sections: [
      {
        items: [
          { href: "/listings", labelKey: "nav.all_listings" },
          // Offices, objects and garages: the same list, its own kind
          // picked (2026-10-21), named as bina.az names them (owner,
          // 2026-10-21: "let's also add offices"; "two types: office and
          // objects").
          { href: "/listings?category=commercial&property_kind=office", labelKey: "nav.offices" },
          { href: "/listings?category=commercial&property_kind=obyekt", labelKey: "nav.obyekts" },
          { href: "/listings?category=garage", labelKey: "nav.garages" },
          { href: "/find", labelKey: "nav.find", feature: "home_finder" },
          { href: "/alerts", labelKey: "nav.alerts" },
          { href: "/auctions", labelKey: "sa.nav" },
        ],
      },
      { headingKey: "nav.compare", items: COMPARE_TABS },
    ],
  },
  { key: "map", href: "/map", labelKey: "nav.map", sections: [{ items: MAP_TABS }] },
  {
    key: "market",
    href: "/trends",
    labelKey: "nav.market",
    sections: [{
      items: [
        { href: "/trends", labelKey: "footer.trends" },
        { href: "/residences", labelKey: "nav.residences" },
        { href: "/tikinti-sirketleri", labelKey: "nav.developers" },
        { href: "/tikinti-sirketleri/teklifler", labelKey: "nav.developer_offers" },
        { href: "/reytinq/rayonlar", labelKey: "footer.rankings" },
        { href: "/invest", labelKey: "nav.invest" },
        { href: "/trends/investor", labelKey: "nav.investor_compare" },
        { href: "/sual", labelKey: "nav.questions" },
      ],
    }],
  },
  {
    key: "company",
    href: "/about",
    labelKey: "nav.company",
    sections: [{
      items: [
        { href: "/about", labelKey: "footer.about" },
        { href: "/methodology", labelKey: "nav.how_we_value" },
        { href: "/faq", labelKey: "nav.faq" },
        // Mid-list, not first (owner, 2026-10-07).
        { href: "/pricing", labelKey: "nav.pricing" },
        { href: "/blog", labelKey: "footer.blog" },
        { href: "/whats-new", labelKey: "wn.nav" },
        { href: "/contact", labelKey: "footer.contact" },
      ],
    }],
  },
];
